export type LoadingZone = "downtown" | "maple_heights";

/** Returned by {@link showDeferredLoading}; pass to {@link hideDeferredLoading}. */
export type LoadingHandle = { readonly token: symbol };

let overlay: HTMLElement | null = null;
let spinner: HTMLElement | null = null;
const tokens = new Set<symbol>();
let sessionToken: symbol | null = null;

function ensure(): { overlay: HTMLElement; spinner: HTMLElement } {
  if (!overlay) overlay = document.getElementById("game-loading-overlay");
  if (!spinner) spinner = document.getElementById("world-spinner");
  if (!overlay || !spinner) {
    throw new Error("world-loading: #game-loading-overlay missing from index.html");
  }
  return { overlay, spinner };
}

function applyZone(zone: LoadingZone): void {
  const { spinner: sp } = ensure();
  sp.classList.remove("downtown", "maple_heights");
  sp.classList.add(zone);
}

function syncVisible(): void {
  const { overlay: el } = ensure();
  if (tokens.size === 0) {
    el.classList.remove("visible");
    el.setAttribute("aria-busy", "false");
    return;
  }
  el.classList.add("visible");
  el.setAttribute("aria-busy", "true");
}

/** Session bootstrap / zone transit — only one session loader at a time. */
export function showSessionLoading(zone: LoadingZone = "downtown"): void {
  if (sessionToken) tokens.delete(sessionToken);
  sessionToken = Symbol();
  tokens.add(sessionToken);
  applyZone(zone);
  syncVisible();
}

export function hideSessionLoading(): void {
  if (sessionToken) {
    tokens.delete(sessionToken);
    sessionToken = null;
  }
  syncVisible();
}

/** Short-lived overlay (dialog, etc.) — does not clear session loader. */
export function showDeferredLoading(zone: LoadingZone = "downtown"): LoadingHandle {
  const token = Symbol();
  tokens.add(token);
  applyZone(zone);
  syncVisible();
  return { token, zone };
}

export function hideDeferredLoading(handle: LoadingHandle | null | undefined): void {
  if (!handle) return;
  tokens.delete(handle.token);
  syncVisible();
}

/** Clears short-lived loaders; keeps an active session transit loader if any. */
export function hideAllDeferredLoading(): void {
  if (sessionToken) {
    const keep = sessionToken;
    tokens.clear();
    tokens.add(keep);
  } else {
    tokens.clear();
  }
  syncVisible();
}

/** @deprecated Use showSessionLoading or showDeferredLoading */
export function showWorldLoading(zone: LoadingZone = "downtown"): void {
  showSessionLoading(zone);
}

/** @deprecated Use hideSessionLoading or hideDeferredLoading */
export function hideWorldLoading(): void {
  hideSessionLoading();
}

export async function withSessionLoading<T>(
  zone: LoadingZone,
  fn: () => Promise<T>
): Promise<T> {
  showSessionLoading(zone);
  try {
    return await fn();
  } finally {
    hideSessionLoading();
  }
}

/** True while any loader token keeps #game-loading-overlay visible. */
export function isGameLoadingOverlayVisible(): boolean {
  return tokens.size > 0;
}
